import React, { StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { boot } from "./app/boot/boot";
import { AppUnauthenticated } from "./app/unauthenticated/AppUnauthenticated";
import {
  OidcAccessTokenProvider,
  OidcAuthenticationService,
  OidcProvider,
  OidcUserProvider,
} from "firefly-shared-ts-authentication-oidc";
import { WebStorageStateStore } from "oidc-client";
import { AccessTokenContextProvider } from "shared-ts-utils-authentication";
import { _throw, NullError } from "shared-ts-utils-core";
import {
  AppBoot,
  CenteredCircularProgress,
  UserSettingsLoadingPage,
} from "shared-ts-ui-app-boot";
import { ChimneyInsightsBootResult } from "./app/boot/ChimneyInsightsBootResult";
import { WindowSizeProvider } from "shared-ts-ui-responsive";
import { I18NextProvider } from "shared-ts-utils-i18n";
import { languages } from "./languages";
import { appInfo, appName } from "./appInfo";
import {
  ServerSettingsStorage,
  UserSettingsProvider,
} from "shared-ts-utils-user-settings";
import {
  AppThemeInitializer,
  AppThemeProvider,
} from "shared-ts-ui-app-theme-provider";
import { MuiLocalizationProvider } from "shared-ts-mui";
import { AirmontLogoImg } from "shared-ts-ui-airmont";
import {
  PostHogConsentProvider,
  PostHogIdentifier,
  PostHogInitializer,
} from "shared-ts-posthog";
import { darkThemeOptions, lightThemeOptions } from "./app/theme";
import { LanguageUserSettingProvider } from "shared-ts-language";
import { LocaleProvider } from "shared-ts-locale";
import { EnvironmentProvider } from "firefly-shared-ts-customer-environment";
import { SystemDao } from "firefly-chimney-insights-ts-domain";
import { Settings } from "luxon";
import { SentryConsentProvider, SentryInitializer } from "shared-ts-sentry";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { WhatsNewProvider } from "chimney-insights-ts-whats-new";
import { CustomerSettingsContextProvider } from "firefly-shared-ts-customer-settings";
import { AppInfoProvider } from "shared-ts-app-info";
import { NotificationContextProvider } from "shared-ts-ui-notification";
import { AppLayout } from "shared-ts-ui-app-layout";
import {
  MunicipalityContextProvider,
  OrganizationProvider,
} from "firefly-shared-ts-domain";
import { additionalGlobalStylesProvider } from "./app/additionalGlobalStyles";
import { AppBootComponent } from "./app/AppBootComponent";
import i18next from "i18next";
import { AppRouter } from "./app/AppRouter";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
});

const authenticationService = new OidcAuthenticationService({
  authority: "https://auth.airmontcloud.net",
  client_id: "Firefly.CustomerWeb",
  redirect_uri: window.location.origin + "/authentication/login-callback",
  scope: "openid FirehiveAPI", //scope dictates the "aud" claim(s) (audience) included in the access_token
  response_type: "id_token token",
  automaticSilentRenew: true,
  includeIdTokenInSilentRenew: true,
  userStore: new WebStorageStateStore({
    prefix: "Firefly.CustomerWeb",
  }),
});

const i18nFallbackLng = {
  "nb-NO": ["no", "en"],
  "nn-NO": ["no", "en"],
  "en-US": ["en"],
};

const rootElement = document.getElementById("root");
const root = createRoot(rootElement ?? _throw(new NullError("rootElement"))); // createRoot(container!) if you use TypeScript
const systemDao = new SystemDao();
root.render(
  <StrictMode>
    <Suspense fallback={<CenteredCircularProgress />}>
      <QueryClientProvider client={queryClient}>
        <AppInfoProvider
          appInfo={appInfo}
          getBuildNumber={systemDao.getBuildNumber}
        >
          {(appInfo) => {
            Settings.throwOnInvalid =
              appInfo.buildVersion === "local-development";
            return (
              <UserSettingsProvider>
                {(settings) => {
                  return (
                    <AppThemeProvider
                      initialTheme={settings.readString("theme")}
                      themes={{
                        dark: darkThemeOptions,
                        light: lightThemeOptions,
                      }}
                    >
                      <AppLayout
                        additionalGlobalStyles={additionalGlobalStylesProvider}
                      >
                        <WindowSizeProvider>
                          <I18NextProvider
                            language={
                              settings.readString("language") ?? "nb-NO"
                            }
                            languages={languages}
                            fallbackLng={i18nFallbackLng}
                            useHttpBackend
                            debug={true}
                          >
                            <OidcProvider instance={authenticationService}>
                              <OidcAccessTokenProvider>
                                {(token) => (
                                  <AccessTokenContextProvider token={token}>
                                    <AppBoot
                                      bootPage={({ messages, error }) => (
                                        <AppBootComponent
                                          messages={messages}
                                          error={error}
                                        />
                                      )}
                                      boot={boot}
                                      app={(bootResult) => {
                                        const chimneyInsightsBootResult =
                                          bootResult as ChimneyInsightsBootResult;
                                        return (
                                          <OidcUserProvider
                                            user={
                                              chimneyInsightsBootResult.user
                                            }
                                          >
                                            <UserSettingsProvider
                                              storage={
                                                new ServerSettingsStorage({
                                                  accessToken:
                                                    chimneyInsightsBootResult
                                                      .user.accessToken,
                                                })
                                              }
                                              loadingComponent={
                                                <UserSettingsLoadingPage />
                                              }
                                            >
                                              {(settings) => {
                                                return (
                                                  <LanguageUserSettingProvider
                                                    defaultLanguage={"nb-NO"}
                                                  >
                                                    {(language) => {
                                                      return (
                                                        <LocaleProvider
                                                          locale={language}
                                                        >
                                                          <MuiLocalizationProvider
                                                            language={language}
                                                          >
                                                            <AppThemeInitializer>
                                                              <WhatsNewProvider>
                                                                <SentryConsentProvider>
                                                                  <SentryInitializer
                                                                    dsn={
                                                                      "https://4af676ad030f48b88b608fde4678ee0d@o4504604658761728.ingest.sentry.io/4504604702212096"
                                                                    }
                                                                  >
                                                                    <PostHogConsentProvider>
                                                                      <PostHogInitializer
                                                                        apiKey={
                                                                          "phc_ah4lqv2x9krqEkmIcqzQpTUDIxIsxSvk5EGz42CFLux"
                                                                        }
                                                                        options={{
                                                                          api_host:
                                                                            "https://eu.i.posthog.com",
                                                                        }}
                                                                      >
                                                                        <PostHogIdentifier
                                                                          user={
                                                                            chimneyInsightsBootResult.user
                                                                          }
                                                                        >
                                                                          <EnvironmentProvider
                                                                            initialUserSettings={
                                                                              settings
                                                                            }
                                                                            environments={
                                                                              chimneyInsightsBootResult.customerEnvironments
                                                                            }
                                                                          >
                                                                            {(
                                                                              customerEnvironment
                                                                            ) => {
                                                                              Sentry.setTags(
                                                                                {
                                                                                  customerEnvironment:
                                                                                    customerEnvironment.id,
                                                                                }
                                                                              );
                                                                              return (
                                                                                <OrganizationProvider
                                                                                  loadingComponent={
                                                                                    <AppBootComponent
                                                                                      messages={[
                                                                                        i18next.t(
                                                                                          "Loading preferences",
                                                                                          {
                                                                                            ns: "chimney-insights-ts-translations",
                                                                                          }
                                                                                        ),
                                                                                      ]}
                                                                                    />
                                                                                  }
                                                                                >
                                                                                  <CustomerSettingsContextProvider>
                                                                                    {(
                                                                                      customerSettingsQueryResult
                                                                                    ) => {
                                                                                      return (
                                                                                        <MunicipalityContextProvider
                                                                                          initialMunicipalities={
                                                                                            chimneyInsightsBootResult.municipalities
                                                                                          }
                                                                                        >
                                                                                          <NotificationContextProvider>
                                                                                            <AppRouter
                                                                                              bootResult={
                                                                                                chimneyInsightsBootResult
                                                                                              }
                                                                                            />
                                                                                          </NotificationContextProvider>
                                                                                        </MunicipalityContextProvider>
                                                                                      );
                                                                                    }}
                                                                                  </CustomerSettingsContextProvider>
                                                                                </OrganizationProvider>
                                                                              );
                                                                            }}
                                                                          </EnvironmentProvider>
                                                                        </PostHogIdentifier>
                                                                      </PostHogInitializer>
                                                                    </PostHogConsentProvider>
                                                                  </SentryInitializer>
                                                                </SentryConsentProvider>
                                                              </WhatsNewProvider>
                                                            </AppThemeInitializer>
                                                          </MuiLocalizationProvider>
                                                        </LocaleProvider>
                                                      );
                                                    }}
                                                  </LanguageUserSettingProvider>
                                                );
                                              }}
                                            </UserSettingsProvider>
                                          </OidcUserProvider>
                                        );
                                      }}
                                      appWhenUnauthenticated={() => {
                                        return <AppUnauthenticated />;
                                      }}
                                    />
                                  </AccessTokenContextProvider>
                                )}
                              </OidcAccessTokenProvider>
                            </OidcProvider>
                          </I18NextProvider>

                          <ReactQueryDevtools
                            initialIsOpen={false}
                            buttonPosition={"top-right"}
                          />
                        </WindowSizeProvider>
                      </AppLayout>
                    </AppThemeProvider>
                  );
                }}
              </UserSettingsProvider>
            );
          }}
        </AppInfoProvider>
      </QueryClientProvider>
    </Suspense>
  </StrictMode>
);
